/** @file unknownNodeWarnings: one UNKNOWN_NODE_IN_CLIP per node a clip names that the puppet lacks. */
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';

/** Every node reference of `clip` with its path: key nodes, flex parents, event nodes, then tracks. */
function references(clipName: string, clip: Clip): [string, string][] {
  const base = `/clips/${clipName}`;
  const refs: [string, string][] = [];
  clip.keys.forEach((key, i) => {
    for (const [node, k] of Object.entries(key.nodes)) {
      refs.push([node, `${base}/keys/${i}/nodes/${node}`]);
      if (typeof k.parent === 'string') refs.push([k.parent, `${base}/keys/${i}/nodes/${node}/parent`]);
    }
    (key.events ?? []).forEach((e, j) => { if (e.node !== undefined) refs.push([e.node, `${base}/keys/${i}/events/${j}/node`]); });
  });
  for (const node of Object.keys(clip.tracks ?? {})) refs.push([node, `${base}/tracks/${node}`]);
  return refs;
}

/** One warning per unknown node (at its first reference); missing nodes are skipped at playback (design 4.4). */
export function unknownNodeWarnings(puppet: Pick<Puppet, 'nodes'>, clipName: string, clip: Clip): Warning[] {
  const seen = new Set<string>();
  return references(clipName, clip).flatMap(([node, path]): Warning[] => {
    if (Object.hasOwn(puppet.nodes, node) || seen.has(node)) return [];
    seen.add(node);
    return [{
      code: 'UNKNOWN_NODE_IN_CLIP', path, params: { clip: clipName, node },
      message: `clip ${clipName} names node ${node}, which the puppet lacks; playback skips it`,
      hint: 'node.add the node, or rename the clip nodes (library.apply nodeMap)',
    }];
  });
}
